import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Mail, KeyRound, Lock, CheckCircle2 } from "lucide-react";
import {
  InputOTP, InputOTPGroup, InputOTPSlot,
} from "../components/ui/input-otp";

export default function ForgotPassword() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [loading, setLoading] = useState(false);

  const requestOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("OTP sent — check your inbox");
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) return toast.error("Enter the 6-digit OTP");
    setLoading(true);
    try {
      await api.post("/auth/verify-otp", { email, otp });
      toast.success("OTP verified");
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const resetPwd = async (e) => {
    e.preventDefault();
    if (pwd !== pwd2) return toast.error("Passwords do not match");
    if (pwd.length < 6) return toast.error("Password must be at least 6 chars");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { email, otp, new_password: pwd });
      toast.success("Password updated. Please log in.");
      nav("/login");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 sm:p-6">
      <div className="w-full max-w-md animate-slide-up">
        <Link to="/login" className="inline-flex items-center text-xs text-muted-foreground hover:text-foreground mb-8" data-testid="back-to-login">
          <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back to sign in
        </Link>

        <div className="flex items-center gap-2 mb-6">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                s <= step ? "bg-primary" : "bg-border"
              }`}
            />
          ))}
        </div>

        {step === 1 && (
          <>
            <div className="w-12 h-12 rounded-full bg-primary/10 text-primary grid place-items-center mb-5">
              <Mail className="w-5 h-5" />
            </div>
            <h2 className="font-heading text-3xl font-semibold mb-2 tracking-tight">Reset your password</h2>
            <p className="text-sm text-muted-foreground mb-8">
              Enter your email and we'll send you a 6-digit OTP to verify it's you.
            </p>
            <form onSubmit={requestOtp} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
                <Input
                  id="email"
                  data-testid="forgot-email-input"
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@united-group.in"
                  className="mt-2 h-11"
                />
              </div>
              <Button data-testid="send-otp-btn" disabled={loading} type="submit" className="w-full h-11">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Send OTP
              </Button>
            </form>
          </>
        )}

        {step === 2 && (
          <>
            <div className="w-12 h-12 rounded-full bg-primary/10 text-primary grid place-items-center mb-5">
              <KeyRound className="w-5 h-5" />
            </div>
            <h2 className="font-heading text-3xl font-semibold mb-2 tracking-tight">Enter OTP</h2>
            <p className="text-sm text-muted-foreground mb-8">
              We sent a 6-digit code to <span className="text-foreground font-medium">{email}</span>. It expires in 10 minutes.
            </p>
            <form onSubmit={verifyOtp} className="space-y-6">
              <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-input">
                <InputOTPGroup>
                  {[0,1,2,3,4,5].map(i => <InputOTPSlot key={i} index={i} />)}
                </InputOTPGroup>
              </InputOTP>
              <Button data-testid="verify-otp-btn" disabled={loading} type="submit" className="w-full h-11">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Verify
              </Button>
              <button type="button" onClick={() => setStep(1)} className="text-xs text-muted-foreground hover:text-foreground">
                Use a different email
              </button>
            </form>
          </>
        )}

        {step === 3 && (
          <>
            <div className="w-12 h-12 rounded-full bg-primary/10 text-primary grid place-items-center mb-5">
              <Lock className="w-5 h-5" />
            </div>
            <h2 className="font-heading text-3xl font-semibold mb-2 tracking-tight">New password</h2>
            <p className="text-sm text-muted-foreground mb-8">
              Choose a strong password (at least 6 characters).
            </p>
            <form onSubmit={resetPwd} className="space-y-5">
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">New password</Label>
                <Input data-testid="new-password-input" type="password" required value={pwd} onChange={(e) => setPwd(e.target.value)} className="mt-2 h-11" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Confirm password</Label>
                <Input data-testid="confirm-password-input" type="password" required value={pwd2} onChange={(e) => setPwd2(e.target.value)} className="mt-2 h-11" />
              </div>
              <Button data-testid="reset-password-btn" disabled={loading} type="submit" className="w-full h-11">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Set new password
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
